import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "src/providers/AuthProvider";

import logoWhite from "src/images/paylinq-logo-white.png"; //
import homeIcon from "src/images/ni-home.png";
import accountsIcon from "src/images/ni-accounts.png";
import customersIcon from "src/images/ni-customers.png";
import invoicesIcon from "src/images/ni-invoices.png";
import inventoryIcon from "src/images/ni-inventory.png";
import paymentsIcon from "src/images/ni-payments.png";
import walletIcon from "src/images/ni-wallet.png";
import expenseTrackerIcon from "src/images/ni-expense-tracker.png";
import settlementsIcon from "src/images/ni-settlements.png";
import payoutsIcon from "src/images/ni-payouts.png";
import settingsIcon from "src/images/ni-settings.png";
import LogoutIcon from "src/images/logout-icon.png";
import LockIcon from "src/images/lock-icon.png";
import { cn, getCustomizationValue, getFromLocalStorage, setToLocalStorage } from "src/lib/utils";
import { CollapseIcon } from "src/svg";
import { Sidebar as SideBarComp, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import useHasPermissions from "src/hooks/useHasPermissions";

const Sidebar = () => {
	const { logout, user } = useAuth();
	const isMobile = useIsMobile();

	const [collapsed, setCollapsed] = useState(() => getFromLocalStorage("collapsed", false));

	useEffect(() => {
		setToLocalStorage("collapsed", collapsed);
	}, [collapsed]);

	const navLinks = [
		{
			path: "/dashboard",
			name: "Dashboard",
			icon: <img src={homeIcon} alt="Home Icon" />,
		},
		...(user?.organization_type_name === "Schools"
			? [
					{
						path: "/customer",
						name: "Customer",
						icon: <img src={customersIcon} alt={`Customer Icon`} />,
						children: [
							{
								path: "/customers",
								name: getCustomizationValue(user, "customer_label"),
							},
							{
								path: "/sub-accounts",
								name: getCustomizationValue(user, "sub_account_label", true),
							},
						],
					},
				]
			: [
					{
						path: "/customers",
						name: getCustomizationValue(user, "customer_label"),
						icon: <img src={customersIcon} alt={`${getCustomizationValue(user, "customer_label")} Icon`} />,
					},
				]),
		{
			path: "/inventory",
			name: getCustomizationValue(user, "inventory_label", true),
			icon: <img src={inventoryIcon} alt={cn(getCustomizationValue(user, "inventory_label", true), " Icon")} />,
			children: [
				{
					path: "/inventory",
					name: "Items",
				},
				{
					path: "/inventory/logs",
					name: cn(getCustomizationValue(user, "inventory_label", true), " Logs"),
				},
				{
					path: "/inventory/requests",
					name: cn(getCustomizationValue(user, "inventory_label", true), " Requests"),
					permission: "view_store_requests",
				},
			],
		},
		{
			path: "invoices",
			name: getCustomizationValue(user, "invoice_label"),
			icon: <img src={invoicesIcon} alt={cn(getCustomizationValue(user, "invoice_label"), " Icon")} />,
			children: [
				{
					path: "/invoices",
					name: getCustomizationValue(user, "invoice_label"),
					permission: "view_invoices",
				},
				{
					path: "/invoice-groups",
					name: cn(getCustomizationValue(user, "invoice_group_label")),
				},
			],
		},
		{
			path: "/payments",
			name: "Payments",
			icon: <img src={paymentsIcon} alt="Payments Icon" />,
			permission: "view_payment",
		},
		{
			path: "/wallet",
			name: "Wallet",
			icon: <img src={walletIcon} alt="Wallet Icon" />,
			permission: "view_payment",
		},
		{
			path: "/settlements",
			name: "Settlements",
			icon: <img src={settlementsIcon} alt="Settlements Icon" />,
			permission: "view_settlements",
		},
		{
			path: "/reconciliation",
			name: "Reconciliation",
			icon: <img src={settlementsIcon} alt="Settlements Icon" />,
			permission: "view_reconciliation",
		},
		{
			path: "/accounts",
			name: "Bank Accounts",
			icon: <img src={accountsIcon} alt="Accounts Icon" />,
		},
		{
			path: "/payouts",
			name: "Payouts",
			icon: <img src={payoutsIcon} alt="Payouts Icon" />,
			lockIcon: <img src={LockIcon} alt="Lock icon" />,
			disabled: true,
		},
		{
			path: "",
			name: "Expense Tracker",
			icon: <img src={expenseTrackerIcon} alt="Expense Tracker Icon" />,
			lockIcon: <img src={LockIcon} alt="Lock icon" />,
			disabled: true,
		},
		{
			path: "/team",
			name: "Team Management",
			icon: <img src={expenseTrackerIcon} alt="Expense Tracker Icon" />,
		},
		{
			path: "users",
			name: "Settings",
			icon: <img src={settingsIcon} alt="Settings Icon" />,
			children: [
				{
					path: "/profile",
					name: "Profile",
				},
				{
					path: "/settings",
					name: "Business Settings",
					permission: "update_business_settings",
				},
				{
					path: "/tags",
					name: "Tag Settings",
				},
				{
					path: "/roles",
					name: "Roles & Permissions",
				},
				{
					path: "/warehouse",
					name: "Warehouse & Storefront",
				},
				{
					path: "/notifications",
					name: "Notification Settings",
				},
				{
					path: "/account",
					name: "Account Tier",
					permission: "view_business_tier",
				},
			],
		},
		{
			onClick: logout,
			name: "Logout",
			icon: <img src={LogoutIcon} alt="Settings Icon" />,
		},
	];

	return (
		<SideBarComp
			backgroundColor="#0C1F55"
			width="250px"
			collapsedWidth="100px"
			customBreakPoint="1000px"
			defaultCollapsed={!isMobile && collapsed}
			rootStyles={{ paddingBlock: 20, backgroundColor: "#0C1F55" }}
		>
			<div
				className={cn(
					"tw-grid tw-mx-4 tw-mb-5",
					collapsed ? "tw-gap-4 *:tw-mx-auto" : "tw-gap-1 tw-grid-cols-[1fr,max-content] tw-items-center",
				)}
			>
				<Link to={"/"} className={cn(collapsed ? "tw-order-2" : "tw-gap-2")}>
					<img src={logoWhite} className="tw-max-w-[150px] tw-w-full" alt="" />
				</Link>
				<button
					className={cn("tw-max-[1000px]:tw-hidden", collapsed && "tw-order-1 tw-mx-auto")}
					onClick={() => setCollapsed((prevState) => !prevState)}
				>
					<CollapseIcon className={cn(collapsed ? "tw-rotate-180 tw-transition-transform" : "tw-transition-transform", "tw-size-6")} />
				</button>
			</div>
			<Menu rootStyles={{ "&>ul": { display: "grid" } }}>
				{navLinks.map((link, index) => (
					<CustomMenuComponent key={index} {...link} />
				))}
			</Menu>
		</SideBarComp>
	);
};

const CustomMenuComponent = ({ path, name, icon, onClick, children, disabled = false, lockIcon, permission }) => {
	const { pathname } = useLocation();
	const hasPermission = useHasPermissions(permission);

	if (!hasPermission) return null;

	if (children) {
		return (
			<SubMenu label={name} disabled={disabled} icon={icon}>
				{children.map((child, index) => (
					<CustomSubMenuComponent key={index} {...child} />
				))}
			</SubMenu>
		);
	}

	return (
		<MenuItem
			title={name}
			{...(onClick ? { onClick } : { component: <Link to={path} /> })}
			active={pathname === path}
			icon={icon}
			disabled={disabled}
		>
			{name} {lockIcon}
		</MenuItem>
	);
};

const CustomSubMenuComponent = ({ name, path, permission }) => {
	const { pathname } = useLocation();
	const hasPermission = useHasPermissions(permission);

	if (!hasPermission) return null;

	return (
		<MenuItem component={<Link to={path} />} active={pathname === path}>
			{name}
		</MenuItem>
	);
};

const useIsMobile = (breakpoint = 1000) => {
	const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < breakpoint);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [breakpoint]);

	return isMobile;
};

export default Sidebar;
